// Handyman validation helpers
// Pure functions for form validation and conditional logic

import { SERVICE_TO_FLOW_MAP, DETAILED_SUBFLOWS } from './handymanData';

/**
 * Check if a quantity value is valid
 * @param {string} quantity - Quantity value to validate
 * @param {Array<string>} validValues - Valid quantity options
 * @returns {boolean}
 */
export const isQuantityValid = (quantity, validValues = ['1', '2-3', '4-6', '7+']) => {
  return validValues.includes(quantity);
};

/**
 * Check if a service selection is complete enough to proceed
 * @param {Array<string>} services - Selected services
 * @returns {boolean}
 */
export const hasServicesSelected = (services) => {
  return Array.isArray(services) && services.length > 0;
};

/**
 * Get the list of triggered sub-flows from selected services
 * @param {Array<string>} selectedServices - Selected service values
 * @returns {Array<string>} - List of triggered flow names
 */
export const getTriggeredSubflows = (selectedServices) => {
  if (!Array.isArray(selectedServices)) return [];
  
  const flows = new Set();
  selectedServices.forEach(serviceValue => {
    const flow = SERVICE_TO_FLOW_MAP[serviceValue];
    if (flow) {
      flows.add(flow);
    }
  });
  return Array.from(flows);
};

/**
 * Check if any services trigger detailed sub-flows
 * @param {Array<string>} selectedServices - Selected service values
 * @returns {boolean}
 */
export const hasDetailedSubflows = (selectedServices) => {
  const triggeredFlows = getTriggeredSubflows(selectedServices);
  return triggeredFlows.length > 0;
};

/**
 * Check if services need the generic project_size question
 * (only when no detailed sub-flows are triggered)
 * @param {Array<string>} selectedServices - Selected service values
 * @returns {boolean}
 */
export const needsProjectSizeQuestion = (selectedServices) => {
  if (!hasServicesSelected(selectedServices)) return false;
  return !hasDetailedSubflows(selectedServices);
};

/**
 * Check if a conditional question should be shown based on showIf/showIfIncludes rules
 * @param {Object} question - Question definition with showIf/showIfIncludes
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const shouldShowQuestion = (question, answers) => {
  // No conditions means always show
  if (!question.showIf && !question.showIfIncludes) return true;
  
  // Check showIf (exact match)
  if (question.showIf) {
    for (const [key, value] of Object.entries(question.showIf)) {
      const answer = answers[key];
      // Handle array values (checkbox type)
      if (Array.isArray(answer)) {
        if (!answer.includes(value)) return false;
      } else {
        if (answer !== value) return false;
      }
    }
  }
  
  // Check showIfIncludes (array contains)
  if (question.showIfIncludes) {
    for (const [key, value] of Object.entries(question.showIfIncludes)) {
      const answer = answers[key];
      if (!Array.isArray(answer)) return false;
      if (!answer.includes(value)) return false;
    }
  }
  
  return true;
};

/**
 * Filter questions to only those that should be visible
 * @param {Array<Object>} questions - List of question definitions
 * @param {Object} answers - Current answers
 * @returns {Array<Object>}
 */
export const getVisibleQuestions = (questions, answers) => {
  return questions.filter(q => shouldShowQuestion(q, answers));
};

/**
 * Check if a question has been answered
 * @param {Object} question - Question definition
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const isQuestionAnswered = (question, answers) => {
  const answer = answers[question.id];
  
  if (question.type === 'text') {
    // Text questions are optional unless explicitly marked required
    return true;
  }
  
  if (question.type === 'checkbox') {
    return Array.isArray(answer) && answer.length > 0;
  }
  
  if (question.type === 'radio') {
    return !!answer;
  }
  
  if (question.type === 'collapsible_categories') {
    return Array.isArray(answer) && answer.length > 0;
  }
  
  return !!answer;
};

/**
 * Check if all required questions in a flow have been answered
 * @param {Array<Object>} questions - List of question definitions
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const isFlowComplete = (questions, answers) => {
  const visibleQuestions = getVisibleQuestions(questions, answers);
  
  // Filter to required questions only (text questions are optional)
  const requiredQuestions = visibleQuestions.filter(q => q.type !== 'text');
  
  return requiredQuestions.every(q => isQuestionAnswered(q, answers));
};

/**
 * Get validation messages for incomplete fields
 * @param {Array<Object>} questions - List of question definitions
 * @param {Object} answers - Current answers
 * @returns {Array<{questionId: string, message: string}>}
 */
export const getValidationErrors = (questions, answers) => {
  const errors = [];
  const visibleQuestions = getVisibleQuestions(questions, answers);
  
  visibleQuestions.forEach(question => {
    // Skip text questions (optional)
    if (question.type === 'text') return;
    
    if (!isQuestionAnswered(question, answers)) {
      errors.push({
        questionId: question.id,
        message: `Please answer: ${question.question}`
      });
    }
  });
  
  return errors;
};

/**
 * Check if interior repair flow should show blinds quantity question
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const shouldShowBlindsQuantity = (answers) => {
  return answers.interior_repair_services === 'hang_blinds';
};

/**
 * Check if interior repair flow should show outlets quantity question
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const shouldShowOutletsQuantity = (answers) => {
  return answers.interior_repair_services === 'replace_outlets';
};

/**
 * Check if interior repair flow should show rental turnover scope question
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const shouldShowRentalTurnoverScope = (answers) => {
  return answers.interior_repair_services === 'rental_turnover';
};

/**
 * Check if mounting flow should show TV size question
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const shouldShowTvSize = (answers) => {
  const mountItems = answers.mount_items || [];
  return mountItems.includes('tv_mount');
};

/**
 * Check if mounting flow should show furniture anchoring question
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const shouldShowFurnitureAnchoring = (answers) => {
  const mountItems = answers.mount_items || [];
  return mountItems.includes('furniture');
};

/**
 * Check if mounting flow should show other description question
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const shouldShowOtherMountDescription = (answers) => {
  const mountItems = answers.mount_items || [];
  return mountItems.includes('other_mounting');
};

/**
 * Check if doors flow should show exterior door question
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const shouldShowExteriorDoorQuestion = (answers) => {
  const doorWork = answers.door_lock_work || [];
  return doorWork.includes('interior_door_install');
};

/**
 * Check if doors flow should show framing modification question
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const shouldShowFramingQuestion = (answers) => {
  const doorWork = answers.door_lock_work || [];
  return doorWork.includes('exterior_door_install');
};

/**
 * Check if carpentry flow should show drywall finishing question
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const shouldShowDrywallFinishing = (answers) => {
  const carpentryWork = answers.carpentry_work || [];
  return carpentryWork.includes('drywall_repair');
};

/**
 * Check if electrical flow should show ceiling fixture question
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const shouldShowCeilingFixture = (answers) => {
  const electricalWork = answers.electrical_work || [];
  return electricalWork.includes('light_fixture');
};

/**
 * Check if plumbing flow should show active leak question
 * @param {Object} answers - Current answers
 * @returns {boolean}
 */
export const shouldShowActiveLeak = (answers) => {
  const plumbingWork = answers.plumbing_work || [];
  return plumbingWork.includes('leak_repair');
};

/**
 * Validate handyman answers before submission
 * @param {Object} answers - All wizard answers
 * @returns {{isValid: boolean, errors: Array}}
 */
export const validateHandymanAnswers = (answers) => {
  const errors = [];
  
  // Must have at least one service selected
  if (!hasServicesSelected(answers.handyman_services)) {
    errors.push({
      field: 'handyman_services',
      message: 'Please select at least one service'
    });
  }
  
  // Must have timeline selected
  if (!answers.timeline) {
    errors.push({
      field: 'timeline',
      message: 'Please select when you need this done'
    });
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};
